import logging from '@sstdev/lib_logging';
import globalConfig from '../../globalConfig';

/**
 * Truncates the collection down to `maxSize - maxDeletePerTruncationPass` records (a buffer
 * below the limit so we don't truncate on every sync). Records currently visible in any
 * active dbView (passed in `excludedIds`) are protected and only deleted as a last resort
 * when the collection is still over the hard `maxSize` after evictables are exhausted.
 *
 * Within the evictable set, rows tagged with `meta.forTempQueryResult` (Go Fetch / paged
 * temp rows) are removed before untagged resident-sync rows — honors #332 AC4.
 *
 * @param {number} maxSize - Hard upper bound on collection size.
 * @param {Set<string>} [excludedIds] - `_id`s of records to protect from eviction.
 * @returns {Promise<{ deleted: number }>}
 */
export default _private => {
    const { startPromise, collection, maxDeletePerPass: injectedMaxDeletePerPass } = _private;
    return (maxSize, excludedIds = new Set()) =>
        startPromise.then(
            () =>
                new Promise((resolve, reject) => {
                    try {
                        const maxDeletePerPass =
                            injectedMaxDeletePerPass ?? globalConfig().maxDeletePerTruncationPass;
                        // Buffer below maxSize so we don't truncate on every sync. When
                        // maxDeletePerPass >= maxSize (misconfig — e.g. runtime config lowered
                        // maxRecordsInLimitedSizeCollection below the per-pass cap), fall back
                        // to targeting maxSize so the loop's `currentSize <= target` exit fires
                        // at the hard bound instead of running until the cap or list exhausts.
                        const target = maxDeletePerPass >= maxSize ? maxSize : maxSize - maxDeletePerPass;

                        const data = collection
                            .chain()
                            .simplesort('meta.serverModifiedTime', { desc: false })
                            .data();

                        // Partition oldest-first sorted data into three groups:
                        //   1. evictableTagged   — meta.forTempQueryResult set (Go Fetch temp rows)
                        //   2. evictableUntagged — resident sync rows
                        //   3. protectedDocs     — visible in an active dbView (last resort)
                        // Eviction order: tagged → untagged → protected. Honors #332 AC4.
                        const evictableTagged = [];
                        const evictableUntagged = [];
                        const protectedDocs = [];
                        for (const doc of data) {
                            if (!doc || typeof doc !== 'object') continue;
                            if (excludedIds.has(doc._id)) protectedDocs.push(doc);
                            else if (doc.meta?.forTempQueryResult) evictableTagged.push(doc);
                            else evictableUntagged.push(doc);
                        }
                        const evictable = [...evictableTagged, ...evictableUntagged];

                        let deleted = 0;
                        let currentSize = data.length;

                        for (const doc of evictable) {
                            if (deleted >= maxDeletePerPass) break;
                            if (currentSize <= target) break;
                            try {
                                collection.remove(doc);
                                deleted++;
                                currentSize--;
                            } catch (err) {
                                logging.error(err);
                            }
                        }

                        if (currentSize > maxSize) {
                            for (const doc of protectedDocs) {
                                if (deleted >= maxDeletePerPass) break;
                                if (currentSize <= maxSize) break;
                                try {
                                    collection.remove(doc);
                                    deleted++;
                                    currentSize--;
                                } catch (err) {
                                    logging.error(err);
                                }
                            }
                        }

                        resolve({ deleted });
                    } catch (err) {
                        reject(err);
                    }
                })
        );
};
